
import { defineComponent, onMounted, PropType, reactive, ref, toRefs, watch } from 'vue'
import EditableBlock from '@/components/EditableBlock.vue'
import FileDrop from '@/components/FileDrop.vue'
import EditableBlockInput from '@/components/EditableBlockInput.vue'
import { useStore } from 'vuex'
import { CtProduct, CtStockItem } from '@/models/interfaces/content-type'
import EditableBlockTextfield from '@/components/EditableBlockTextfield.vue'
import ImageDisplay from '@/components/ImageDisplay.vue'
import draggable from 'vuedraggable'
import { useRouter } from 'vue-router'
import Tooltip from '@/components/Tooltip.vue'
import Clipable from '@/components/Clipable.vue'
import BarcodeIcon from '@/components/Icons/BarcodeIcon.vue'
import Barcode from '@/components/Barcode.vue'
import tooltips from '@/constants/tooltips'
import { QSpinnerTail, useQuasar } from 'quasar'
import { validateEan } from '@/utils/input'

export default defineComponent({
  name: 'ProductInformation',
  components: {
    Barcode,
    BarcodeIcon,
    Clipable, Tooltip, ImageDisplay, EditableBlockTextfield, EditableBlockInput, FileDrop, EditableBlock, draggable
  },
  emits: ['update:product'],
  props: {
    product: {
      type: Object as PropType<CtProduct>,
      default: undefined,
    },
    components: {
      type: Array as PropType<Array<CtStockItem>>
    },
    creating: {
      type: Boolean
    },
  },

  setup (props, { emit }) {
    const $q = useQuasar()
    const $store = useStore()
    const $directr = $store.getters['Services/Directr']
    const $router = useRouter()

    const state = reactive({
      // components: null as CtStockItem | null,
      images: [] as string[],
      activeImage: '',
      dragOptions: {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost'
      },
    })

    const showBarcodeSku = ref(false)
    const showBarcodeEan = ref(false)
    const doUploadImage = () => {
      (document.querySelector('.image-upload-product input') as HTMLInputElement)?.click()
    }

    async function resolveImage (imageArr) {
      if (!Array.isArray(imageArr) || !imageArr.length) {
        state.images = []
        return
      }

      state.images = (await Promise.all(imageArr.map(async imgId => await $directr.get('asset.product.image', imgId)))).map(({ items }) => items[0]?.data).filter(f => !!f)
      state.activeImage = state.images[0]
    }

    const setActiveImage = (index?) => {
      if (!state.activeImage || state.activeImage === '') {
        state.activeImage = state.images[0]
      } else {
        state.activeImage = state.images[index]
      }
    }

    const removeImage = (index, editState) => {
      editState.images?.splice(index, 1)
      void resolveImage(editState.images)
    }

    async function fileInputHandler (data, product) {
      try {
        if (!data.fileName || !data.file) return
        const { items: [asset] } = await $directr.put('asset', 'asset.product.image', {
          fileName: data.fileName,
          file: data.file
        })

        if (!product) return
        if (!product.images) product.images = []
        product?.images.push(asset.sys.id)
      } catch (e) {
        console.error('error creating asset', e)
      }
      void resolveImage(product.images)
    }

    const dragImage = (editState, event) => {
      const image = editState.images.splice(event.moved.oldIndex, 1)[0]
      editState.images.splice(event.moved.newIndex, 0, image)
    }

    watch(() => props.product?.images, () => resolveImage(props.product?.images))

    const emitUpdate = (productId, $event) => {
      $q.loading.show({ spinner: QSpinnerTail })
      emit('update:product', productId, $event,)
      // if (props.creating) {
      //   // $q.notify({
      //   //   message: 'This product is in draft until you add Components. Orders referencing this product will not be accepted while it is in draft.',
      //   //   color: 'warning',
      //   //   icon: 'warning',
      //   //   position: 'bottom',
      //   //   textColor: 'black'
      //   // })
      // }
      setTimeout(() => {
        $q.loading.hide()
      }, 1000)
    }

    const cancelEditing = (product) => {
      if (props.creating) {
        void $router.push({ name: 'ProductsOverview' })
        return
      }
      void resolveImage(product.images)
    }

    const getEan = (eanArr) => {
      return eanArr
    }

    const setEan = (event, editState) => {
      if (!editState.ean) editState.ean = []
      event.map(e => {
        if (editState.ean.includes(e.label)) {
          $q.notify({
            type: 'negative',
            position: 'bottom',
            message: 'The product already includes this ean',
            classes: 'custom-notify error'
          })
          return
        }
        if (e.label) editState.ean.push(e.label)
      })
      return true
    }

    onMounted(() => {
      if (props.product?.images) {
        void resolveImage(props.product?.images)
      }
    })

    return {
      ...toRefs(state),
      fileInputHandler,
      emitUpdate,
      setActiveImage,
      removeImage,
      cancelEditing,
      dragImage,
      getEan,
      setEan,
      showBarcodeSku,
      showBarcodeEan,
      tooltips,
      doUploadImage,
      validateEan
    }
  }
})
